import Date from "@components/Atoms/Date";
import type { CommentDateTime, CommentUser } from "@core-ui/CommentBlock";
import { cn } from "@core-ui/utils/cn";
import { AgentMessageIndicator } from "@ext/agent/components/AgentMessageIndicator";
import t from "@ext/localization/locale/translate";
import { CommentInput } from "@ext/markdown/elements/comment/edit/components/Popover/CommentInput";
import { CommentTitleActions } from "@ext/markdown/elements/comment/edit/components/Popover/CommentTitleActions";
import type { FocusPosition, JSONContent } from "@tiptap/core";
import { Avatar, AvatarFallback, getAvatarFallback } from "@ui-kit/Avatar";
import { Label } from "@ui-kit/Label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { type HTMLAttributes, memo, type RefObject, useCallback, useState } from "react";

interface CommentMessageProps extends Omit<HTMLAttributes<HTMLDivElement>, "content"> {
	index: number;
	user: CommentUser;
	isCurrentUser: boolean;
	content?: JSONContent[];
	editable?: boolean;
	date?: CommentDateTime;
	last?: boolean;
	autofocus?: FocusPosition;
	showName?: boolean;
	showAvatar?: boolean;
	containerRef?: RefObject<HTMLDivElement>;
	assistedByAgent?: boolean;
	onConfirm: (index: number, content: JSONContent[], hide: boolean) => void;
	onDelete?: (index: number) => void;
}

export const CommentMessage = memo((props: CommentMessageProps) => {
	const {
		index,
		user,
		isCurrentUser,
		content,
		date,
		editable = false,
		autofocus = false,
		showName = true,
		showAvatar = true,
		assistedByAgent,
		onConfirm,
		className,
		onDelete,
		last,
		containerRef,
		...otherProps
	} = props;
	const [isEditing, setIsEditing] = useState(editable);
	const isOldContent = !!content;
	const name = user.name || t("comment-author-you");
	const nameLabel = <Label className="truncate whitespace-nowrap text-primary-fg">{name}</Label>;

	const onConfirmComment = useCallback(
		(content: JSONContent[]) => {
			if (isOldContent) setIsEditing(false);
			onConfirm(index, content, !isOldContent);
		},
		[onConfirm, index, isOldContent],
	);

	const onCancelComment = useCallback(() => {
		setIsEditing(false);
	}, []);

	return (
		<div
			className={cn(
				"group/message flex w-full transition-colors gap-2 items-center",
				isEditing && "bg-secondary-bg/50 border-t border-secondary-border",
				isEditing && !last && "border-b",
				!showName && !showAvatar ? "px-4 py-3" : "p-3 pl-4",
				className,
			)}
			{...otherProps}
		>
			{showAvatar && (
				<Avatar
					className="shrink-0 self-baseline border-none"
					indicator={
						assistedByAgent && (
							<Tooltip>
								<TooltipTrigger asChild>
									<AgentMessageIndicator className="rounded-bl-none" />
								</TooltipTrigger>
								<TooltipContent>{t("comments.assisted-by-agent")}</TooltipContent>
							</Tooltip>
						)
					}
					size="xs"
				>
					<AvatarFallback uniqueId={user.mail}>{getAvatarFallback(name)}</AvatarFallback>
				</Avatar>
			)}
			<div className="flex w-full min-w-0 flex-col gap-1 self-baseline text-left">
				{showName && (
					<div className="flex h-5 items-center gap-2 text-sm font-medium text-primary-fg">
						{user.mail ? (
							<Tooltip>
								<TooltipTrigger asChild>{nameLabel}</TooltipTrigger>
								<TooltipContent>{user.mail}</TooltipContent>
							</Tooltip>
						) : (
							nameLabel
						)}
						<Date
							className="flex-shrink-0 truncate whitespace-nowrap text-xs font-normal text-muted"
							date={date}
						/>
						<CommentTitleActions
							isCurrentUser={isCurrentUser}
							onClickDelete={onDelete ? () => onDelete(index) : undefined}
							onClickEdit={() => setIsEditing(true)}
						/>
					</div>
				)}
				<CommentInput
					autofocus={autofocus || isOldContent}
					containerRef={containerRef}
					content={content}
					editable={isEditing}
					isNewComment={!content}
					onCancel={isOldContent && onCancelComment}
					onConfirm={onConfirmComment}
				/>
			</div>
		</div>
	);
});
